// Re-read the cart before swapping. No prices or inventory are set client-side.
export async function upgradeCart(api, offer) {
  const before = await api.read();
  const clean = item => !item.selling_plan_allocation && !Object.keys(item.properties || {}).length;
  const source = before.items.find(item => item.key === offer.key);
  if (!source || source.variant_id !== offer.sourceId || source.quantity !== offer.quantity || !clean(source)) {
    throw new Error('Your cart changed. Refresh the page before upgrading.');
  }
  if (before.items.filter(item => item.variant_id === offer.sourceId).length !== 1 || before.items.some(item => item.variant_id === offer.targetId)) {
    throw new Error('Please review the items already in your cart before upgrading.');
  }
  const sourceRows = cart => cart.items.filter(item => item.variant_id === offer.sourceId && clean(item));
  const targetRows = cart => cart.items.filter(item => item.variant_id === offer.targetId && clean(item));
  const amount = rows => rows.reduce((sum, item) => sum + item.quantity, 0);
  let result;
  try {
    result = await api.update({[source.key]: 0, [String(offer.targetId)]: offer.quantity});
  } catch (error) {
    // A lost response does not imply that the write failed. Never repeat the swap blindly.
    try { result = await api.read(); } catch { throw new Error('We could not confirm the update. Refresh your cart before continuing.'); }
  }
  const originals = sourceRows(result), bundles = targetRows(result);
  if (amount(originals) === 0 && amount(bundles) === offer.quantity) return result;
  if (amount(originals) === offer.quantity && amount(bundles) === 0) {
    throw new Error('This bundle could not be added in the selected quantity. Your original item is still in your cart.');
  }
  // Recover an incomplete replacement. Only touch the source and the newly requested bundle.
  if (originals.length <= 1 && bundles.length <= 1 && amount(originals) <= offer.quantity && amount(bundles) <= offer.quantity) {
    const restore = {};
    for (const item of bundles) restore[item.key] = 0;
    restore[originals[0]?.key || String(offer.sourceId)] = offer.quantity;
    try {
      const recovered = await api.update(restore);
      if (amount(sourceRows(recovered)) === offer.quantity && !targetRows(recovered).length) {
        throw Object.assign(new Error('The bundle is unavailable in this quantity. Your original item has been restored. Refresh your cart to continue.'), {recovered: true});
      }
    } catch (error) { if (error.recovered) throw error; }
  }
  throw new Error('The upgrade was not completed. Refresh and review your cart before checkout.');
}

if (typeof document !== 'undefined') {
  let busy = false;
  let needsRefresh = false;
  function prepare(form) {
    const select = form.querySelector('select');
    const button = form.querySelector('[data-upgrade-submit]');
    const option = select.selectedOptions[0];
    button.disabled = !select.value || busy || needsRefresh;
    const price = form.querySelector('[data-upgrade-price]');
    const text = select.value ? `Bundle total ${option.dataset.total} · Difference ${option.dataset.extra}` : '';
    if (price.textContent !== text) price.textContent = text;
  }
  function scan() { document.querySelectorAll('[data-bm-cart-upgrade]').forEach(prepare); }
  document.addEventListener('change', event => {
    const form = event.target.closest('[data-bm-cart-upgrade]');
    if (form) prepare(form);
  });
  document.addEventListener('submit', async event => {
    const form = event.target.closest('[data-bm-cart-upgrade]');
    if (!form) return;
    event.preventDefault();
    if (busy || needsRefresh || !form.reportValidity()) return;
    const select = form.querySelector('select');
    const offer = {key: form.dataset.sourceKey, sourceId: Number(form.dataset.sourceId), targetId: Number(select.value), quantity: Number(form.dataset.quantity)};
    const status = form.querySelector('.bm-cart-offer__status');
    const surface = form.closest('.bm-cart-surface');
    const root = form.dataset.root || '/';
    const request = async (path, data) => {
      const response = await fetch(root + path, {method: data ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', headers: {'Accept': 'application/json', ...(data ? {'Content-Type': 'application/json'} : {})}, ...(data ? {body: JSON.stringify({updates: data})} : {})});
      const body = await response.json();
      if (!response.ok || !Array.isArray(body.items)) throw new Error('Cart update failed');
      return body;
    };
    busy = true;
    status.textContent = 'Updating your setup…';
    surface.setAttribute('aria-busy', 'true');
    const controls = [...surface.querySelectorAll('button, input, select, textarea')];
    const states = controls.map(control => control.disabled);
    controls.forEach(control => { control.disabled = true; });
    try {
      await upgradeCart({read: () => request('cart.js'), update: updates => request('cart/update.js', updates)}, offer);
      // Refresh the cart only after the bundle swap is verified.
      window.location.reload();
    } catch (error) {
      needsRefresh = true;
      status.textContent = error.message;
      const refresh = document.createElement('a');
      refresh.href = window.location.href;
      refresh.textContent = 'Refresh cart';
      refresh.className = 'bm-cart-refresh';
      status.append(' ', refresh);
      // Do not allow checkout or another upgrade against potentially stale cart state.
      controls.forEach((control, index) => { control.disabled = states[index]; });
      surface.querySelectorAll('[data-upgrade-submit], [name="checkout"]').forEach(control => { control.disabled = true; });
      surface.removeAttribute('aria-busy');
      busy = false;
    }
  });
  new MutationObserver(scan).observe(document.documentElement, {childList:true, subtree:true});
  scan();
}
